/**
 * Where to send someone whose session just ended — and how to get them back.
 *
 * A forced logout used to navigate to a bare `/login`, which threw away the
 * address it happened on. That is survivable on `/graph`; on a consent URL it
 * is not, because `/authorize?client_id=…&redirect_uri=…` is the ONLY place
 * the app's callback exists. The app redirected server-side and kept no state,
 * so once that query string is gone nothing can reconstruct it: the person
 * logs in, lands on `/graph`, and has to walk back to the app themselves while
 * the app sits on `?error=auth_failed`.
 *
 * `next` is the convention already in use — Logout reads it, admin-frontend's
 * RequireAdmin writes it, Login has read it all along with nothing in this
 * frontend ever writing one.
 */

/** Pages where returning to the current address after login is a loop, not a return. */
const NO_RETURN = ['/login', '/logout', '/register', '/forgot-password', '/reset-password'];

/**
 * The login URL to send someone to, carrying where they were.
 *
 * @param {{pathname: string, search: string}} at — the address being left
 * @param {{expired?: boolean}} opts — expired sessions say so, so Login can explain itself
 */
export function loginUrlFor(at, { expired = false } = {}) {
  const pathname = at?.pathname || '/';
  const search = at?.search || '';
  const params = new URLSearchParams();

  // Encoded ONCE. URLSearchParams decodes once on the way out, so a second
  // encode here is what turns a nested `redirect_uri=https%3A%2F%2F…` into a
  // truncated one on the way back.
  if (!NO_RETURN.includes(pathname) && pathname !== '/') {
    params.set('next', pathname + search);
  } else {
    // ALREADY ON A SIGN-IN PAGE: keep the `next` it carries. A stray 401 on
    // /login?next=… (a request still in flight from the page before) used to
    // force a logout to a bare /login, and the app's callback was gone.
    const kept = new URLSearchParams(search).get('next');
    if (kept) params.set('next', kept);
  }
  if (expired) params.set('expired', 'true');

  const q = params.toString();
  return q ? `/login?${q}` : '/login';
}

/**
 * The `next` a login page should act on.
 *
 * `searchParams.get('next')` has already decoded once, so the value is ready to
 * use. Decoding it a second time unescapes the query string INSIDE it — which
 * is exactly where an app's `redirect_uri` lives — so it is done only for a
 * legacy double-encoded value, recognisable because it has not decoded into a
 * path or a URL yet.
 */
export function resolveNext(raw) {
  if (!raw) return null;
  if (raw.startsWith('/') || raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}
